// React and Core Libraries
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

// External Libraries
import $ from "jquery";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { toastr } from "react-redux-toastr";

// Redux Actions - Sub-domain
import {
  getRemediationData,
  removeRemediationData,
  addRemediationData,
  /*remediationDataReset,*/
  getRemediationAction,
  remediationDataTotal,
  getRemediationActionType,
  getRemediationActionRating,
  getProcessList,
  getContainersList,
  getContainersBodyList,
  getRemediationActionNote,
} from "../../actions/sub-domain";

// Redux Actions - Summary
import { fetchPendingCount, fetchRemidiationList } from "../../actions/summary";

// Redux Actions - Remediation Mode
import { getOverviewList } from "../../actions/remediationMode";

// Custom Components
import Pagination from "../Pagination/Pagination";

// Asset Images - Main Icons
import img from "../../assets/images/Icon_Top_Action_Pane.svg";
import iconDrag from "../../assets/images/icon_drag.svg";
import icon from "../../assets/images/Btn_View_Process.svg";

// Asset Images - Action Types
import task from "../../assets/images/Selected_Task.svg";
import ticket from "../../assets/images/Selected_Ticket.svg";
import traning from "../../assets/images/Selected_Training.svg";

// Asset Images - UI Elements
import imgadded from "../../assets/images/non-adedd.png";
import historyimg from "../../assets/images/history.png";
import typesadded from "../../assets/images/types_added.png";
import totalAdd from "../../assets/images/total-added.png";
import refersh from "../../assets/images/refresh_selction.png";
import iconSetting from "../../assets/images/Icon_process.png";
import imgtable from "../../assets/images/table_sign.png";
import imgselect from "../../assets/images/select_box.png";
import removeimg from "../../assets/images/remove.png";
import imgRemediationHistory from "../../assets/images/Remediation_History.svg";

// ====================================================================================================
// COMPONENT DEFINITION & INITIALIZATION
// ====================================================================================================

const RightNav = (props) => {
  // ====================================================================================================
  // HOOKS & DISPATCHERS
  // ====================================================================================================
  const location = useLocation();
  const dispatch = useDispatch();

  // ====================================================================================================
  // REDUX SELECTORS
  // ====================================================================================================
  const id = useSelector(
    (state) =>
      state?.summary?.localClient?._id ||
      state?.summary?.clients?.data?.[0]?._id
  );

  // ====================================================================================================
  // STATE MANAGEMENT
  // ====================================================================================================
  // Navigation & UI State
  const [pathname, setPathname] = useState(location.pathname);
  const [sessionScreen, setsessionScreen] = useState("");

  // Pagination & Data Display
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [category] = useState("all"); // setCategory unused - consider if needed

  // Form & Action State
  const [selectedValue, setSelectedValue] = useState("pleaseSelect");
  const [forValue, setForValue] = useState("forever");
  const [lookalikeAppId] = useState([]); // Used in submit function
  const [actionNote, setActionNote] = useState("");
  const [action, setAction] = useState(""); // Used in submit function

  // Modal State
  const [isModaltwo, setModalTwo] = useState(false);

  // Debounce states for notes input optimization
  const [noteTimeouts, setNoteTimeouts] = useState({});
  const [localNotes, setLocalNotes] = useState({});

  // Local state for optimistic UI updates to prevent dropdown issues
  const [localActionTypes, setLocalActionTypes] = useState({});

  // ====================================================================================================
  // COMPUTED VALUES & DERIVED STATE
  // ====================================================================================================
  let modeValue = useSelector(
    (state) => state?.summary?.modes?.data || "assessment-mode"
  );
  let remediationList = useSelector(
    (state) => state?.auth?.remediationListAll?.data
  );
  let remediationTotal = useSelector(
    (state) => state?.auth?.remediationTotal?.data
  );
  let pendingTask = useSelector((state) => state?.summary?.pendingCount);

  // Get the remediation mode overview data for processed items count
  let remediationModeList = useSelector(
    (state) => state?.remediationMode?.list?.data
  );

  // Calculate the count of processed items (items in Remediation Mode Overview)
  const processedItemsCount = useMemo(() => {
    if (!remediationModeList?.tickets) return 0;
    // Count all tickets in the Remediation Mode Overview
    return remediationModeList.tickets.length;
  }, [remediationModeList]);
  let remediationlistTicket = useSelector(
    (state) => state?.summary?.remediationList
  );
  let containersData = useSelector(
    (state) => state?.subDomain?.processContainerList?.data
  );
  let containersDataBody = useSelector(
    (state) => state?.subDomain?.processContainerBodyList?.data
  );

  // ====================================================================================================
  // EFFECT HOOKS
  // ====================================================================================================
  // Update pathname when location changes
  useEffect(() => {
    setPathname(location.pathname);
  }, [location.pathname]);

  // Fetch pending task count when organization ID changes
  useEffect(() => {
    if (id) {
      fetchPendingCount(id, dispatch);
    }
  }, [id, dispatch]);

  // Fetch remediation list when organization ID changes
  useEffect(() => {
    if (id) {
      fetchRemidiationList(id, dispatch);
    }
  }, [id, dispatch]);

  // Fetch remediation data when pagination or filters change
  useEffect(() => {
    if (id) {
      getRemediationData(dispatch, id, page, category);
    }
  }, [id, page, category, dispatch]);

  // Update total items count (database items only)
  useEffect(() => {
    setTotalItems(remediationList?.totalRecords || 0);
  }, [remediationList?.totalRecords]);

  // Clear optimistic state when fresh data arrives from server
  useEffect(() => {
    if (remediationList?.data) {
      // Clear any optimistic updates for items that now have server data
      setLocalActionTypes((prev) => {
        const newState = { ...prev };
        let hasChanges = false;

        // Check each optimistic update against server data
        Object.keys(newState).forEach((itemId) => {
          const serverItem = remediationList.data.find(
            (item) => item._id === itemId
          );
          if (serverItem && serverItem.actionType === newState[itemId]) {
            // Server data matches our optimistic update, safe to clear
            delete newState[itemId];
            hasChanges = true;
          }
        });
        return hasChanges ? newState : prev;
      });
    }
  }, [remediationList?.data]);

  // Fetch remediation totals when organization ID changes
  useEffect(() => {
    if (id) {
      remediationDataTotal(dispatch, id);
    }
  }, [id, dispatch]);

  // Fetch containers list when organization ID changes
  useEffect(() => {
    if (id) {
      getContainersList(dispatch, id);
    }
  }, [id, dispatch]);

  // ====================================================================================================
  // EVENT HANDLERS & UTILITY FUNCTIONS
  // ====================================================================================================
  // Cart/Remediation List Management Functions
  /**
   * Remove item from database with confirmation dialog
   * @param {string} _id - Item ID to remove
   */
  const deleteCart = (_id) => (e) => {
    $.confirm({
      title: "Are you sure?",
      content:
        "Do you really want to remove this from the Remediation List Panel?",
      type: "red",
      buttons: {
        ok: {
          text: "Remove",
          btnClass: "btn-danger",
          keys: ["enter"],
          action: function () {
            e.preventDefault();
            removeRemediationData(_id, dispatch, id);
          },
        },
        cancel: function () {
          // User cancelled - no action needed
        },
      },
    });
  };

  // Process Management Functions
  /**
   * Process all current remediation items in the panel (set isProcessed: true)
   * Handles the "Process Current List" functionality. Process items in Remediation List Panel by changing isProcessed from false to true
   */
  const handleClick = () => {
    // Check if there are items to process
    if (!remediationList?.data || remediationList.data.length === 0) {
      $.confirm({
        title: "No Items",
        content: "No items found in the remediation list to process.",
        type: "blue",
        buttons: {
          ok: {
            text: "OK",
            btnClass: "btn-primary",
          },
        },
      });
      return;
    }

    //console.log("Processing current remediation list items:", remediationList.data.length);

    // Use the original working approach
    getProcessList(dispatch, id);

    // Refresh the remediation data after processing
    setTimeout(() => {
      getRemediationData(dispatch, id, page, category);
    }, 1000);
  };

  // Input Management Functions (with debouncing)
  /**
   * Handle notes input with debouncing to avoid excessive API calls
   * @param {string} elemId - Element ID to update notes for
   * @param {string} value - New note value
   */
  const handleNotesChange = (elemId, value) => {
    // Update local state immediately for UI responsiveness
    setLocalNotes((prev) => ({
      ...prev,
      [elemId]: value,
    }));

    // Clear existing timeout for this element
    if (noteTimeouts[elemId]) {
      clearTimeout(noteTimeouts[elemId]);
    }

    // Set new timeout to save after user stops typing
    const timeoutId = setTimeout(() => {
      if (value !== "" && value !== undefined) {
        let data = {
          note: value,
          organisationId: id,
          showSuccessMessage: true, // Only show success message on deliberate save
        };
        getRemediationActionNote(dispatch, elemId, data, id);
      }
      // Clean up timeout
      setNoteTimeouts((prev) => {
        const newTimeouts = { ...prev };
        delete newTimeouts[elemId];
        return newTimeouts;
      });
    }, 4000); // Wait 4 seconds after user stops typing

    // Update timeouts state
    setNoteTimeouts((prev) => ({
      ...prev,
      [elemId]: timeoutId,
    }));
  };

  // Dropdown Change Handlers
  const handleActionTypeChange = (elemId, value) => {
    //console.log("Action change - elemId:", elemId, "value:", value);
    if (value !== "" && value !== undefined) {
      // OPTIMISTIC UPDATE: Update local state immediately for instant UI feedback
      setLocalActionTypes((prev) => ({
        ...prev,
        [elemId]: value,
      }));

      let data = {
        actionType: value,
        refreshList: false,
      };
      getRemediationActionType(dispatch, elemId, data, id)
        .then((result) => {
          //console.log("Action type update result:", result);
          if (result && result.data.status === 200) {
            // Refresh the counters but with a small delay to avoid interfering with dropdown state
            setTimeout(() => {
              remediationDataTotal(dispatch, id);
            }, 100); // Small delay to let optimistic state settle

            //console.log("Action type updated successfully, keeping optimistic state");
          }
        })
        .catch((error) => {
          console.error("Error updating action type:", error);
          // Revert optimistic update on error
          setLocalActionTypes((prev) => {
            const newState = { ...prev };
            delete newState[elemId];
            return newState;
          });
        });
    }
  };

  const handleRatingChange = (elemId, value) => {
    //console.log("Rating change - elemId:", elemId, "value:", value);
    if (value !== "" && value !== undefined) {
      let data = {
        rating: value,
        organisationId: id, // Add organization ID like action type API
      };
      getRemediationActionRating(dispatch, elemId, data, id)
        .then((result) => {
          //console.log("Rating update result:", result);
          // Accept various success indicators
          if (
            result &&
            (result.data.success ||
              result.data.status === 200 ||
              result.status === 200)
          ) {
            // Refresh the panel data
            getRemediationData(dispatch, id, page, category);
            remediationDataTotal(dispatch, id);
            // Refresh the overview list to update the processed items counter
            getOverviewList(id, dispatch, 1, "", "");
          }
        })
        .catch((error) => {
          console.error("Error updating rating:", error);
        });
    }
  };

  // UI Panel Toggle Functions
  /**
   * Toggle main remediation panel visibility
   * @param {Event} e - Click event
   */
  const tabInOut = (e) => {
    e.preventDefault();
    let tab_uppper_button = document.getElementById("right_bar_button");
    let mypanel = document.getElementById("right_side_id");

    tab_uppper_button.classList.toggle("active_circle_two");
    mypanel.classList.toggle("right_side_bar_active");
  };

  /**
   * Toggle remediation list panel and history view
   * @param {Event} e - Click event
   */
  const tabInOuttwo = (e) => {
    e.preventDefault();
    let right_side_list = document.getElementById("right_side_list");
    let tab_uppper_button = document.getElementById("right_bar_button_list");
    let mypanel = document.getElementById("full_list_pageId");
    if (right_side_list) {
      right_side_list.classList.toggle("right_side_list_active");
    }
    if (tab_uppper_button) {
      tab_uppper_button.classList.toggle("active_circle_two");
    }
    if (mypanel) {
      mypanel.classList.toggle("full_list_page_active");
    }
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const [multiCheckTrue, setMultiCheckTrue] = useState(false);

  // Multi-selection and Bulk Actions Functions
  /**
   * Toggle all items selection for bulk operations
   * @param {Array} data - Array of items to select/unselect
   */
  const multicheck = (data) => {
    const allIds = data?.map((item) => item._id);
    if (selectedIds.length > 0 || selectedIds.length == 0) {
      setMultiCheckTrue(true);
      setSelectedIds(allIds);
    }
    if (multiCheckTrue == true) {
      setSelectedIds([]);
      setMultiCheckTrue(false);
    }
  };

  /**
   * Handle single item selection/deselection
   * @param {string} id - Item ID to toggle selection
   */
  const singleClick = (id, data) => {
    setSelectedIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelectedIds, id];
      }
    });
    if (selectedIds.length > 10) {
      setMultiCheckTrue(true);
    } else {
      setMultiCheckTrue(false);
    }
  };

  /**
   * Submit function for modal form submissions
   * Used by risk acceptance and false positive modals
   */
  const submit = () => {
    const data = {
      action: action,
      recordIds: selectedIds == 0 ? lookalikeAppId : selectedIds,
      actionNote: actionNote,
      for: forValue,
    };
    getRemediationAction(dispatch, id, data, page, category);
    setModalTwo("");
    setSelectedValue("pleaseSelect");
    setActionNote("");
    setSelectedIds([]);
  };

  // ====================================================================================================
  // JSX RENDERING
  // ====================================================================================================
  return (
    <>
      {/* Dashboard/Frontpage -  Remediation Status/Pending Sidebar */}
      {/* Commented by Jaya - 03/10/2025
      {(["/summary", "/"].includes(pathname)) && (
        <div className="dashboard_remediation">
          <div className="box cursor" onClick={tabInOuttwo}>
            <div className="circle_box">
              <div className="circle_box_one">
                <img src={historyimg} alt="History" />
              </div>
            </div>
            <p>Remediation Task/actions</p>
            <h3><p>Pending</p>{pendingTask?.totalPedingTasks || 0}</h3>
          </div>
        </div>
      )} */}
      {/* Dashboard/Frontpage - Recommended Actions Sidebar */}
      {modeValue != "settings-mode" ? (
        <>
          {["/summary", "/"].includes(pathname) ||
          modeValue == "remediation-mode" ? (
            <>
              <div className="right_side_bar side_bar" id="right_side_id">
                <div className="up_down_circle" id="right_bar_button">
                  {/* This counter is wrong, fix before implementation */}
                  {/*{totalItems != 0 && modeValue != 'report-mode' && (
                  <span className="counter">{totalItems || 0}</span>
                )}*/}
                  <span onClick={tabInOut}>
                    <img
                      src="/assets/images/icon_drag.svg"
                      className="img-fluid"
                      alt=""
                    />
                  </span>
                </div>
                <div className="tab_boxes tab_boxes_ul">
                  <ul className="nav nav-tabs" id="myTab" role="tablist">
                    <li></li>
                    <li className="nav-item" role="presentation">
                      {/* <a
                        href="javascript:void(0)"
                        className="nav-link active security_check_1"
                        id="home-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#home-tab-pane"
                        type="button"
                        role="tab"
                        aria-controls="home-tab-pane"
                        aria-selected="true"
                      >
                        <span>
                          Check to <span className="d-block">+Add</span>
                        </span>
                      </a> */}
                    </li>
                    <li className="nav-item" role="presentation">
                      <a
                        href="javascript:void(0)"
                        className="nav-link active security_check_1 security_check_img"
                        id="home-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#home-tab-pane"
                        type="button"
                        role="tab"
                        aria-controls="home-tab-pane"
                        aria-selected="true"
                      >
                        <img
                          src="/assets/images/Icon_Drag_2.png"
                          className="img-fluid"
                          alt=""
                        />
                      </a>
                    </li>
                    <li className="nav-item" role="presentation">
                      <a
                        href="javascript:void(0)"
                        className="nav-link text_img_sidebar"
                        id="profile-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#profile-tab-pane"
                        type="button"
                        role="tab"
                        aria-controls="profile-tab-pane"
                        aria-selected="false"
                      >
                        <span>Slide to Proccess</span>
                      </a>
                    </li>
                  </ul>
                  {/* Recommended Actions Panel */}
                  <div className="tab-content" id="myTabContent">
                    <div
                      className="tab-pane fade show active"
                      id="home-tab-pane"
                      role="tabpanel"
                      aria-labelledby="home-tab"
                      tabindex="0"
                    >
                      <h5>
                        {modeValue == "report-mode"
                          ? "Report model action"
                          : "Recommended Actions"}
                      </h5>
                      <div className="row_boxes row_boxes_table">
                        <div className="table_box table_box_rem">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Type</th>
                                {/*<th>Status</th>*/}
                                <th>Description</th>
                                <th>Rating</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {remediationlistTicket?.map((elem) => {
                                return (
                                  <tr>
                                    <td>{elem?.type}</td>
                                    {/*<td>{elem?.status}</td>*/}
                                    <td>{elem?.description || "N/A"}</td>
                                    <td>{elem?.severity}</td>
                                    <td>
                                      <td>
                                        {/* Remove/Cart buttons should only be on individual pages, not in the panel */}
                                      </td>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div
                      className="tab-pane fade"
                      id="contact-tab-pane"
                      role="tabpanel"
                      aria-labelledby="contact-tab"
                      tabindex="0"
                    ></div>
                  </div>
                </div>
              </div>

              {/* Remediation Session History Panel */}
              <div className="full_list_page" id="full_list_pageId">
                {/* Top Bar */}
                <div className="top_bar_list">
                  <img src={imgRemediationHistory} alt="Remediation History" />
                  <p style={{ maxWidth: "130px" }}>
                    Remediation <span>Session History</span>
                  </p>
                  <div className="boxPending">
                    <span>{pendingTask?.totalPedingTasks || 0}</span>
                    <p>Pending Activity</p>
                  </div>
                </div>
                <div className="main_flex_box main_flex_box_2">
                  <div className="first_box" style={{ width: "197px" }}>
                    <div className="up_down_circle_close">
                      <div
                        className="up_down_circle up_down_circle_2"
                        id="right_bar_button_three"
                      >
                        {totalItems != 0 && modeValue != "report-mode" && (
                          <span className="counter">{totalItems || 0}</span>
                        )}
                        <a onClick={tabInOuttwo}>
                          <img
                            src="/assets/images/icon_drag.svg"
                            className="img-fluid"
                            alt=""
                          />
                        </a>
                      </div>
                      <p className="p_absolute">Click to Close</p>
                    </div>
                  </div>
                  <div className="midBox">
                    <div className="table_box">
                      <div className="table-responsive table-icons table-width">
                        <table className="table m-0">
                          <thead>
                            <tr>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                Status
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                Session
                                <span>Reference</span>
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                Session
                                <span>Date</span>
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                <img
                                  src={task}
                                  className="img-fluid"
                                  alt="Task"
                                />
                                Session
                                <span>Tasks</span>
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                <img
                                  src={ticket}
                                  className="img-fluid"
                                  alt="Ticket"
                                />
                                Session
                                <span>Tickets</span>
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                <img
                                  src={traning}
                                  className="img-fluid"
                                  alt="Training"
                                />
                                Session
                                <span>Trainings</span>
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                <img
                                  src="../assets/images/Risk_Manegement_Main.svg"
                                  className="img-fluid"
                                  alt="Risk Management"
                                />
                                Risk
                                <span>Management</span>
                              </th>
                              <th
                                style={{
                                  borderColor: "transparent !important",
                                }}
                              >
                                <img
                                  src={totalAdd}
                                  className="img-fluid"
                                  alt="Total Add"
                                />
                                Totals
                              </th>
                            </tr>
                          </thead>
                        </table>
                      </div>
                      {containersData?.containersList?.map(
                        (container, index) => (
                          <>
                            <div
                              className="table-responsive table-width"
                              key={container?.containerId}
                            >
                              <table className="table m-0">
                                <thead
                                  onClick={() => {
                                    getContainersBodyList(
                                      dispatch,
                                      id,
                                      container?.containerId
                                    );
                                  }}
                                >
                                  <tr>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      <span
                                        className={
                                          container?.status === "Completed"
                                            ? "greyDarkColour"
                                            : "blueColour"
                                        }
                                      >
                                        {container?.status === "Completed"
                                          ? "Done"
                                          : "Pending"}
                                      </span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      #{container?.containerId}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      {new Date(
                                        container?.containerCreationDate
                                      ).toLocaleDateString()}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      {container?.taskCount}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      {container?.ticketCount}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      {container?.trainingCount}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      {container?.riskManagementCount}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      {container?.totalTasksCount}
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    ></th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                        textAlign: "end",
                                        width: "500px",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faBars}
                                        style={{
                                          color: "#5f646d",
                                          fontSize: "21px",
                                          marginLeft: "16px",
                                          marginBottom: "-4px",
                                        }}
                                      />
                                    </th>
                                  </tr>
                                </thead>
                              </table>
                            </div>
                            {containersDataBody?.tasksList?.map((res) => {
                              if (res.containerId === container.containerId) {
                                return (
                                  <div className="table-responsive table-short-width">
                                    <table className="table">
                                      <thead>
                                        <tr>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            Status
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            Description
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            category
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            Action
                                            <span className="d-block">
                                              selected
                                            </span>
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            Season
                                            <span className="d-block">
                                              Pending
                                            </span>
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            Reviews
                                            <span className="d-block">
                                              Action
                                            </span>
                                          </th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr key={res._id}>
                                          <td>
                                            {" "}
                                            <span
                                              className={
                                                container?.status ===
                                                "Completed"
                                                  ? "greyDarkColour"
                                                  : "blueColour"
                                              }
                                            >
                                              {container?.status === "Completed"
                                                ? "Done"
                                                : "Pending"}
                                            </span>
                                          </td>
                                          <td>N/A</td>
                                          <td>{res?.collectionName}</td>
                                          <td>{res?.actionType}</td>
                                          <td>N/A</td>
                                          <td>N/A</td>
                                          <td>N/A</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                );
                              }
                            })}
                          </>
                        )
                      )}
                      {/* <div>*/}
                    </div>
                  </div>
                </div>
                {/* Bottom Bar */}
                <div className="bottom_panel_list justify-content-center align-items-center">
                  <div className="box_left justify-content-center">
                    {/* <div className="left-innerBox-1 left-innerBox">
                          <div className="outer_div">
                            <div className="inner_div">
                              <img src={refersh} className="img-fluid" />
                            </div>
                          </div>
                          <p>
                            Refersh <span>Reviews</span>
                          </p>
                        </div> */}
                    {/* <div className="left-innerBox-2 left-innerBox">
                          <div className="outer_div">
                            <div className="inner_div">
                              <img src={iconSetting} className="img-fluid" />
                            </div>
                          </div>
                          <p style={{ maxWidth: '88px' }}>
                            Process <span>Reviews</span>
                          </p>
                        </div> */}
                    <div
                      className="left-innerBox-3 left-innerBox"
                      id="right_bar_button_three"
                      onClick={tabInOuttwo}
                    >
                      <div className="outer_div">
                        <div className="inner_div">
                          <img
                            src={iconDrag}
                            className="img-fluid"
                            alt="Drag"
                          />
                        </div>
                      </div>
                      <p>
                        Back to <span>Current</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Remediation List Panel Sidebar */}
              {(modeValue == "assessment-mode" ||
                modeValue === "remediation-mode") && (
                <div
                  className="right_side_bar side_bar right_side_list"
                  id="right_side_list"
                >
                  <div
                    className="up_down_circle up_down_circle_two"
                    id="right_bar_button_list"
                  >
                    {totalItems != 0 && modeValue != "report-mode" && (
                      <span className="counter">{totalItems || 0}</span>
                    )}
                    <a onClick={tabInOuttwo}>
                      <img
                        src="/assets/images/icon_drag.svg"
                        className="img-fluid"
                        alt=""
                      />
                    </a>
                  </div>
                  <div className="tab_boxes tab_boxes_ul">
                    <ul className="nav nav-tabs" id="myTab" role="tablist">
                      <li>
                        <img src={img} alt="Tab" />
                      </li>
                      <li className="nav-item" role="presentation">
                        <a
                          href="javascript:void(0)"
                          className="nav-link active security_check_1"
                          id="home-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#home-tab-pane"
                          type="button"
                          role="tab"
                          aria-controls="home-tab-pane"
                          aria-selected="true"
                        >
                          <span>
                            Remediation{" "}
                            <span className="d-block">List Panel</span>
                          </span>
                        </a>
                      </li>
                      <li className="nav-item" role="presentation">
                        <span
                          // href="javascript:void(0)"
                          className="nav-link active security_check_1 security_check_img useToadd"
                          style={{ textAlign: "center" }}
                        >
                          Use +<br />
                          to add
                        </span>
                      </li>
                      <li className="nav-item" role="presentation">
                        <a
                          href="javascript:void(0)"
                          className="nav-link active security_check_1 security_check_img settingIcon"
                        >
                          <img
                            src="/assets/images/Icon_Drag_2.png"
                            className="img-fluid"
                            alt=""
                          />
                        </a>
                      </li>
                      <li className="nav-item" role="presentation">
                        <a
                          href="javascript:void(0)"
                          className="nav-link text_img_sidebar"
                          type="button"
                        >
                          <img
                            src={imgadded}
                            className="img-fluid"
                            alt="Added"
                          />
                          {totalItems > 0 ? (
                            <>
                              <span>Pending</span>
                              <h5>{totalItems}</h5>
                            </>
                          ) : (
                            <>
                              <span>Your list is empty</span>
                              <h5>00</h5>
                            </>
                          )}
                        </a>
                      </li>
                      {/* <li class="nav-item" role="presentation">
                      <a
                        href="javascript:void(0)"
                        class="nav-link text_img_sidebar"
                        type="button"
                      >
                        <h6>{totalItems}</h6>
                        <span>Total</span>
                      </a>
                    </li> */}
                      <li className="nav-item" role="presentation">
                        <a
                          href="javascript:void(0)"
                          className="nav-link text_img_sidebar"
                          type="button"
                        >
                          <img src={icon} className="img-fluid" alt="Icon" />
                        </a>
                      </li>
                      <li
                        className="nav-item view-process"
                        role="presentation"
                        onClick={tabInOuttwo}
                      >
                        <a
                          href="javascript:void(0)"
                          className="nav-link text_img_sidebar"
                          type="button"
                        >
                          <span>View / Process</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              {/* Remediation List Panel */}
              {(modeValue == "assessment-mode" ||
                modeValue === "remediation-mode") && (
                <>
                  {sessionScreen !== "sessionShow" ? (
                    <div className="full_list_page" id="full_list_pageId">
                      <div className="top_bar_list">
                        <img src={img} alt="Remediation" />
                        <p>Remediation List Panel</p>
                      </div>
                      <div className="main_flex_box">
                        <div className="first_box">
                          <div className="up_down_circle_close">
                            <div
                              className="up_down_circle up_down_circle_2"
                              id="right_bar_button_three"
                              onClick={tabInOuttwo}
                            >
                              {totalItems != 0 &&
                                modeValue != "report-mode" && (
                                  <span className="counter">
                                    {totalItems || 0}
                                  </span>
                                )}
                              <span>
                                <img
                                  src="/assets/images/icon_drag.svg"
                                  className="img-fluid"
                                  alt=""
                                />
                              </span>
                            </div>
                            <p className="p_absolute">Click to Close</p>
                          </div>
                          <div
                            className="box cursor"
                            onClick={() => {
                              setsessionScreen("sessionShow");
                            }}
                          >
                            <div className="circle_box">
                              <div className="circle_box_one">
                                <img src={historyimg} alt="History" />
                              </div>
                            </div>
                            <p>View History / Pending</p>
                            <h3>{processedItemsCount}</h3>
                          </div>
                        </div>
                        <div className="midBox">
                          <div className="table_box">
                            <p>Discovered and Added</p>
                            <div className="table-responsive">
                              <img
                                src={imgtable}
                                className="icon-table"
                                alt="Table"
                              />
                              <table className="table">
                                <thead>
                                  <tr>
                                    {(category === "all" ||
                                      category === "risk-accepted" ||
                                      category === "false-positive") && (
                                      <th>
                                        <input
                                          type="checkbox"
                                          checked={
                                            selectedIds == 0
                                              ? false
                                              : multiCheckTrue
                                          }
                                          onClick={() => {
                                            multicheck(remediationList?.data);
                                          }}
                                          className="checkUncheck"
                                        />
                                      </th>
                                    )}
                                    <th>Type</th>
                                    <th>Details</th>
                                    <th>Notes</th>
                                    <th>Rating</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* Show existing remediation items from database */}
                                  {remediationList?.data?.map((elem) => {
                                    return (
                                      <tr key={elem?._id}>
                                        {(category === "all" ||
                                          category === "risk-accepted" ||
                                          category === "false-positive") && (
                                          <td>
                                            <input
                                              onClick={() =>
                                                singleClick(elem?._id)
                                              }
                                              checked={selectedIds?.includes(
                                                elem?._id
                                              )}
                                              type="checkbox"
                                              className="checkUncheck"
                                            />
                                          </td>
                                        )}
                                        <td>{elem?.type}</td>
                                        <td>{elem?.description}</td>
                                        <td>
                                          <input
                                            placeholder="Type here"
                                            value={
                                              localNotes[elem?._id] !==
                                              undefined
                                                ? localNotes[elem?._id]
                                                : elem?.note || ""
                                            }
                                            onChange={(e) => {
                                              handleNotesChange(
                                                elem?._id,
                                                e.target.value
                                              );
                                            }}
                                            className="form-control typeHereInput"
                                          />
                                        </td>
                                        <td>
                                          <div className="select-box">
                                            <select
                                              className="select_box_mytask"
                                              value={elem?.rating}
                                              onChange={(e) => {
                                                //console.log("Rating dropdown - elem._id:", elem?._id, "current rating:", elem?.rating, "new value:", e.target.value);
                                                handleRatingChange(
                                                  elem?._id,
                                                  e.target.value
                                                );
                                              }}
                                            >
                                              <option value="">
                                                Please select
                                              </option>
                                              <option value="High">High</option>
                                              <option value="Low">Low</option>
                                              <option value="Medium">
                                                Medium
                                              </option>
                                            </select>
                                            <div className="select_box_img">
                                              <img
                                                src={imgselect}
                                                alt="Select"
                                              />
                                            </div>
                                          </div>
                                        </td>
                                        <td>
                                          <div className="d-flex">
                                            <div className="select-box">
                                              <select
                                                className="select_box_mytask"
                                                value={
                                                  localActionTypes[elem?._id] ||
                                                  elem?.actionType
                                                }
                                                onChange={(e) => {
                                                  //console.log("Action dropdown - elem._id:", elem?._id, "current actionType:", elem?.actionType, "new value:", e.target.value);
                                                  handleActionTypeChange(
                                                    elem?._id,
                                                    e.target.value
                                                  );
                                                }}
                                              >
                                                <option value="">
                                                  Please select
                                                </option>
                                                <option value="task">
                                                  Assign Task
                                                </option>
                                                <option value="ticket">
                                                  Request Ticket
                                                </option>
                                                <option value="training">
                                                  Request Training
                                                </option>
                                                <option value="riskManagement">
                                                  Risk Management
                                                </option>
                                              </select>
                                              <div className="select_box_img">
                                                <img
                                                  src={imgselect}
                                                  alt="Select"
                                                />
                                              </div>
                                            </div>
                                            <div
                                              className="remove-box"
                                              onClick={deleteCart(elem?._id)}
                                              style={{ cursor: "pointer" }}
                                            >
                                              <div className="box">
                                                <img
                                                  src={removeimg}
                                                  alt="Remove"
                                                />
                                              </div>
                                              <p>Remove</p>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {/* Panel items are stored in database with isProcessed: false */}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="dataTables_paginate">
                            {totalItems > 0 && (
                              <Pagination
                                className="pagination-bar"
                                currentPage={page}
                                totalCount={totalItems}
                                pageSize={10}
                                onPageChange={(page) => {
                                  setPage(page);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Remediation List Panel BOTTOM BAR */}
                      <div className="bottom_panel_list">
                        <div className="box_right">
                          <div className="box-a">
                            <div className="inner_box">
                              <div className="types_added">
                                <div className="boxadded">
                                  <img
                                    src={task}
                                    className="img-fluid"
                                    alt="Task"
                                  />
                                  <h6>{remediationTotal?.task || 0}</h6>
                                  <p>Task Added</p>
                                </div>
                                <div className="boxadded">
                                  <img
                                    src={ticket}
                                    className="img-fluid"
                                    alt="Ticket"
                                  />
                                  <h6>{remediationTotal?.ticket || 0}</h6>
                                  <p>Ticket Added</p>
                                </div>
                                <div className="boxadded">
                                  <img
                                    src={traning}
                                    className="img-fluid"
                                    alt="Training"
                                  />
                                  <h6>{remediationTotal?.training || 0}</h6>
                                  <p>Training Added</p>
                                </div>
                                <div className="boxadded boxadded_1">
                                  <img
                                    src="../assets/images/Risk_Manegement_Main.svg"
                                    style={{ width: "40px" }}
                                    className="img-fluid"
                                    alt="Risk Management"
                                  />
                                  <h6>
                                    {remediationTotal?.riskManagement || 0}
                                  </h6>
                                  <p>Risk Management</p>
                                </div>
                              </div>
                              <div className="added_types">
                                <p>Actions Selected</p>
                                <img
                                  src={typesadded}
                                  className="img-fluid"
                                  alt="Types Added"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="box_left">
                          <div className="left-innerBox box_new">
                            <img
                              src={totalAdd}
                              className="img-fluid"
                              alt="Total Added"
                            />
                            <div>
                              <h5> {totalItems || 0}</h5>
                              <p>TOTAL Added</p>
                            </div>
                          </div>
                          <div
                            className="left-innerBox-1 left-innerBox cursor"
                            onClick={() => {
                              if (selectedIds.length === 0) {
                                alert(
                                  "Please select items to remove first by checking the checkboxes."
                                );
                                return;
                              }

                              $.confirm({
                                title: "Are you sure?",
                                content: `Do you really want to remove ${selectedIds.length} selected item(s) from the Remediation List Panel?`,
                                type: "red",
                                buttons: {
                                  remove: {
                                    text: "Remove",
                                    btnClass: "btn-danger",
                                    keys: ["enter"],
                                    action: function () {
                                      // Remove selected items from database
                                      selectedIds.forEach((itemId) => {
                                        removeRemediationData(
                                          itemId,
                                          dispatch,
                                          id
                                        );
                                      });

                                      // Clear selections
                                      setSelectedIds([]);

                                      // Refresh the list
                                      setTimeout(() => {
                                        fetchRemidiationList(id, dispatch);
                                      }, 500);
                                    },
                                  },
                                  cancel: {
                                    text: "Cancel",
                                    action: function () {
                                      // Do nothing
                                    },
                                  },
                                },
                              });
                            }}
                          >
                            <div className="outer_div">
                              <div className="inner_div">
                                <img
                                  src={refersh}
                                  className="img-fluid"
                                  alt="Refresh"
                                />
                              </div>
                            </div>
                            <p>
                              Remove <span>Selections</span>
                            </p>
                          </div>
                          <div
                            className="left-innerBox-2 left-innerBox cursor"
                            onClick={handleClick}
                          >
                            <div className="outer_div">
                              <div className="inner_div">
                                <img
                                  src={iconSetting}
                                  className="img-fluid"
                                  alt="Settings Icon"
                                />
                              </div>
                            </div>
                            <p>
                              Process <span>Current List</span>
                            </p>
                          </div>
                          <div
                            className="left-innerBox-3 left-innerBox"
                            id="right_bar_button_three"
                            onClick={tabInOuttwo}
                          >
                            <div className="outer_div">
                              <div className="inner_div">
                                <img
                                  src={iconDrag}
                                  className="img-fluid"
                                  alt="Drag Icon"
                                />
                              </div>
                            </div>
                            <p>
                              Close <span>Panel</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`modal fade Form-Wizard-modal ${
                          isModaltwo === "Form Wizard modal two"
                            ? "d-block show"
                            : ""
                        }`}
                      >
                        <div className="modal-dialog modal-lg" id="clientModal">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5
                                className="modal-title"
                                id="exampleModalLabel"
                              >
                                {action == "markAsFalsePositive"
                                  ? "Mark As False Positive"
                                  : "Risk Accepted"}
                              </h5>
                              <button
                                type="button"
                                data-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                  setModalTwo("");
                                  setActionNote("");
                                }}
                              >
                                <span aria-hidden="true">×</span>
                              </button>
                            </div>
                            <div className="modal-body pricing_page modal_mob">
                              <div className="col-xl-12 col-lg-12 col-md-12">
                                <div className="card">
                                  <div className="row clearfix">
                                    <div className="col-md-12">
                                      <label className="col-lg-12 col-md-12">
                                        Detail
                                      </label>
                                      <div className="form-group col-lg-12 col-md-12">
                                        <textarea
                                          onChange={(e) =>
                                            setActionNote(e.target.value)
                                          }
                                          value={actionNote}
                                          className="form-control"
                                          rows="3"
                                        ></textarea>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div
                                      className="col-lg-12 col-md-12"
                                      style={{ marginRight: "28px" }}
                                    >
                                      <button
                                        type="button"
                                        style={{
                                          color: "black",
                                          fontWeight: "bold",
                                        }}
                                        onClick={submit}
                                        className="btn btn-round btn-primary mr-1 pull-right"
                                      >
                                        Submit
                                      </button>
                                      &nbsp;&nbsp;
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setModalTwo("");
                                          setActionNote("");
                                        }}
                                        className="btn btn-round btn-default pull-right"
                                        style={{ marginRight: "10px" }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="full_list_page" id="full_list_pageId">
                      <div className="top_bar_list">
                        <img
                          src={imgRemediationHistory}
                          alt="Remediation Session History"
                        />
                        <p style={{ maxWidth: "130px" }}>
                          Remediation <span>Session History</span>
                        </p>
                        <div className="boxPending">
                          <span>{pendingTask?.totalPedingTasks || 0}</span>
                          <p>Pending Activity</p>
                        </div>
                      </div>
                      <div className="main_flex_box main_flex_box_2">
                        <div className="first_box" style={{ width: "197px" }}>
                          <div className="up_down_circle_close">
                            <div
                              className="up_down_circle up_down_circle_2"
                              id="right_bar_button_three"
                            >
                              {pendingTask?.totalPedingTasks != 0 &&
                                modeValue != "report-mode" && (
                                  <span className="counter">
                                    {" "}
                                    {pendingTask?.totalPedingTasks || 0}
                                  </span>
                                )}
                              <a
                                onClick={() => {
                                  setsessionScreen("");
                                }}
                              >
                                <img
                                  src="/assets/images/icon_drag.svg"
                                  className="img-fluid"
                                  alt=""
                                />
                              </a>
                            </div>
                            <p className="p_absolute">Click to Close</p>
                          </div>
                        </div>
                        <div className="midBox">
                          <div className="table_box">
                            <div className="table-responsive table-icons table-width">
                              <table className="table m-0">
                                <thead>
                                  <tr>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      Status
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      Session
                                      <span>Reference</span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      Session
                                      <span>Date</span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      <img
                                        src={task}
                                        className="img-fluid"
                                        alt="Task"
                                      />
                                      Session
                                      <span>Tasks</span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      <img
                                        src={ticket}
                                        className="img-fluid"
                                        alt="Ticket"
                                      />
                                      Session
                                      <span>Tickets</span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      <img
                                        src={traning}
                                        className="img-fluid"
                                        alt="Training"
                                      />
                                      Session
                                      <span>Trainings</span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      <img
                                        src="../assets/images/Risk_Manegement_Main.svg"
                                        className="img-fluid"
                                        alt="Risk Management"
                                      />
                                      Risk
                                      <span>Management</span>
                                    </th>
                                    <th
                                      style={{
                                        borderColor: "transparent !important",
                                      }}
                                    >
                                      <img
                                        src={totalAdd}
                                        className="img-fluid"
                                        alt="Total Add"
                                      />
                                      Totals
                                    </th>
                                  </tr>
                                </thead>
                              </table>
                            </div>
                            {containersData?.containersList?.map(
                              (container, index) => (
                                <>
                                  <div
                                    className="table-responsive table-width"
                                    key={container?.containerId}
                                  >
                                    <table className="table m-0">
                                      <thead
                                        onClick={() => {
                                          getContainersBodyList(
                                            dispatch,
                                            id,
                                            container?.containerId
                                          );
                                        }}
                                      >
                                        <tr>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            <span
                                              className={
                                                container?.status ===
                                                "Completed"
                                                  ? "greyDarkColour"
                                                  : "blueColour"
                                              }
                                            >
                                              {container?.status === "Completed"
                                                ? "Done"
                                                : "Pending"}
                                            </span>
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            #{container?.containerId}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            {new Date(
                                              container?.containerCreationDate
                                            ).toLocaleDateString()}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            {container?.taskCount}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            {container?.ticketCount}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            {container?.trainingCount}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            {container?.riskManagementCount}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          >
                                            {container?.totalTasksCount}
                                          </th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                            }}
                                          ></th>
                                          <th
                                            style={{
                                              borderColor:
                                                "transparent !important",
                                              textAlign: "end",
                                              width: "500px",
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon={faBars}
                                              style={{
                                                color: "#5f646d",
                                                fontSize: "21px",
                                                marginLeft: "16px",
                                                marginBottom: "-4px",
                                              }}
                                            />
                                          </th>
                                        </tr>
                                      </thead>
                                    </table>
                                  </div>
                                  {containersDataBody?.tasksList?.map((res) => {
                                    if (
                                      res.containerId === container.containerId
                                    ) {
                                      return (
                                        <div className="table-responsive table-short-width">
                                          <table className="table">
                                            <thead>
                                              <tr>
                                                <th
                                                  style={{
                                                    borderColor:
                                                      "transparent !important",
                                                  }}
                                                >
                                                  Status
                                                </th>
                                                <th
                                                  style={{
                                                    borderColor:
                                                      "transparent !important",
                                                  }}
                                                >
                                                  Description
                                                </th>
                                                <th
                                                  style={{
                                                    borderColor:
                                                      "transparent !important",
                                                  }}
                                                >
                                                  category
                                                </th>
                                                <th
                                                  style={{
                                                    borderColor:
                                                      "transparent !important",
                                                  }}
                                                >
                                                  Action
                                                  <span className="d-block">
                                                    selected
                                                  </span>
                                                </th>
                                                <th
                                                  style={{
                                                    borderColor:
                                                      "transparent !important",
                                                  }}
                                                >
                                                  Season
                                                  <span className="d-block">
                                                    Pending
                                                  </span>
                                                </th>
                                                <th
                                                  style={{
                                                    borderColor:
                                                      "transparent !important",
                                                  }}
                                                >
                                                  Reviews
                                                  <span className="d-block">
                                                    Action
                                                  </span>
                                                </th>
                                                <th></th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              <tr key={res._id}>
                                                <td>
                                                  {" "}
                                                  <span
                                                    className={
                                                      container?.status ===
                                                      "Completed"
                                                        ? "greyDarkColour"
                                                        : "blueColour"
                                                    }
                                                  >
                                                    {container?.status ===
                                                    "Completed"
                                                      ? "Done"
                                                      : "Pending"}
                                                  </span>
                                                </td>
                                                <td>N/A</td>
                                                <td>{res?.collectionName}</td>
                                                <td>{res?.actionType}</td>
                                                <td>N/A</td>
                                                <td>N/A</td>
                                                <td>N/A</td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      );
                                    }
                                  })}
                                </>
                              )
                            )}
                            {/* <div>*/}
                          </div>
                        </div>
                      </div>
                      <div className="bottom_panel_list justify-content-center align-items-center">
                        <div className="box_left justify-content-center">
                          {/* <div className="left-innerBox-1 left-innerBox">
                          <div className="outer_div">
                            <div className="inner_div">
                              <img src={refersh} className="img-fluid" />
                            </div>
                          </div>
                          <p>
                            Refersh <span>Reviews</span>
                          </p>
                        </div> */}
                          {/* <div className="left-innerBox-2 left-innerBox">
                          <div className="outer_div">
                            <div className="inner_div">
                              <img src={iconSetting} className="img-fluid" />
                            </div>
                          </div>
                          <p style={{ maxWidth: '88px' }}>
                            Process <span>Reviews</span>
                          </p>
                        </div> */}
                          <div
                            className="left-innerBox-3 left-innerBox"
                            id="right_bar_button_three"
                            onClick={() => {
                              setsessionScreen("");
                            }}
                          >
                            <div className="outer_div">
                              <div className="inner_div">
                                <img
                                  src={iconDrag}
                                  className="img-fluid"
                                  alt="Drag Icon"
                                />
                              </div>
                            </div>
                            <p>
                              Back to <span>Current</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`modal fade Form-Wizard-modal ${
                          isModaltwo === "Form Wizard modal two"
                            ? "d-block show"
                            : ""
                        }`}
                      >
                        <div className="modal-dialog modal-lg" id="clientModal">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5
                                className="modal-title"
                                id="exampleModalLabel"
                              >
                                {action == "markAsFalsePositive"
                                  ? "Mark As False Positive"
                                  : "Risk Accepted"}
                              </h5>
                              <button
                                type="button"
                                data-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                  setModalTwo("");
                                  setActionNote("");
                                }}
                              >
                                <span aria-hidden="true">×</span>
                              </button>
                            </div>
                            <div className="modal-body pricing_page modal_mob">
                              <div className="col-xl-12 col-lg-12 col-md-12">
                                <div className="card">
                                  <div className="row clearfix">
                                    <div className="col-md-12">
                                      <label className="col-lg-12 col-md-12">
                                        Detail
                                      </label>
                                      <div className="form-group col-lg-12 col-md-12">
                                        <textarea
                                          onChange={(e) =>
                                            setActionNote(e.target.value)
                                          }
                                          value={actionNote}
                                          className="form-control"
                                          rows="3"
                                        ></textarea>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div
                                      className="col-lg-12 col-md-12"
                                      style={{ marginRight: "28px" }}
                                    >
                                      <button
                                        type="button"
                                        style={{
                                          color: "black",
                                          fontWeight: "bold",
                                        }}
                                        onClick={submit}
                                        className="btn btn-round btn-primary mr-1 pull-right"
                                      >
                                        Submit
                                      </button>
                                      &nbsp;&nbsp;
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setModalTwo("");
                                          setActionNote("");
                                        }}
                                        className="btn btn-round btn-default pull-right"
                                        style={{ marginRight: "10px" }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <div></div>
        </>
      )}
    </>
  );
};

export default RightNav;
